<template>
  <div class="user-profile-page container-fluid py-4 px-3">
    <div class="row justify-content-center">
      <div class="col-12 col-xl-10">
        <div class="d-flex align-items-center gap-2 mb-4">
          <i class="material-icons text-primary" style="font-size: 36px;">account_circle</i>
          <div>
            <h1 class="h4 mb-0">Mi perfil</h1>
            <p class="text-muted small mb-0">
              Actualiza tu nombre o tu contraseña. El correo y el teléfono solo pueden cambiarse desde administración.
            </p>
          </div>
        </div>

        <v-alert v-if="loadError" type="error" variant="tonal" class="mb-4" closable @click:close="loadError = null">
          {{ loadError }}
        </v-alert>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4 rounded" />

        <template v-else-if="profile">
          <v-row>
            <!-- Solo lectura: cuenta -->
            <v-col cols="12" md="6">
              <v-card class="h-100" elevation="2">
                <v-card-title class="bg-grey-lighten-4 text-subtitle-1 py-3">
                  <i class="material-icons me-2" style="font-size: 20px; vertical-align: middle;">lock</i>
                  Datos de cuenta (solo lectura)
                </v-card-title>
                <v-card-text class="pt-4">
                  <v-text-field
                    :model-value="profile.username || '—'"
                    label="Usuario"
                    variant="outlined"
                    density="comfortable"
                    readonly
                    hide-details="auto"
                    class="mb-3"
                  />
                  <v-text-field
                    :model-value="profile.email || '—'"
                    label="Correo electrónico"
                    variant="outlined"
                    density="comfortable"
                    readonly
                    hide-details="auto"
                    class="mb-3"
                  />
                  <v-text-field
                    :model-value="profile.phone || '—'"
                    label="Teléfono"
                    variant="outlined"
                    density="comfortable"
                    readonly
                    hide-details="auto"
                    class="mb-3"
                  />
                  <v-text-field
                    :model-value="profile.codePhone || '—'"
                    label="Lada / código telefónico"
                    variant="outlined"
                    density="comfortable"
                    readonly
                    hide-details="auto"
                  />
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Nombre -->
            <v-col cols="12" md="6">
              <v-card class="h-100" elevation="2">
                <v-card-title class="bg-grey-lighten-4 text-subtitle-1 py-3">
                  <i class="material-icons me-2" style="font-size: 20px; vertical-align: middle;">edit</i>
                  Nombre y apellidos
                </v-card-title>
                <v-card-text class="pt-4">
                  <v-form ref="profileForm" @submit.prevent="saveProfile">
                    <v-text-field
                      v-model="form.firstName"
                      label="Nombre *"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required]"
                      hide-details="auto"
                      class="mb-3"
                    />
                    <v-text-field
                      v-model="form.lastName"
                      label="Apellidos *"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required]"
                      hide-details="auto"
                      class="mb-4"
                    />
                    <v-btn
                      type="submit"
                      color="primary"
                      variant="flat"
                      :loading="saving"
                      block
                    >
                      <i class="material-icons me-1" style="font-size: 18px; vertical-align: middle;">save</i>
                      Guardar nombre
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Contraseña -->
            <v-col cols="12" md="6">
              <v-card elevation="2" class="h-100">
                <v-card-title class="bg-grey-lighten-4 text-subtitle-1 py-3">
                  <i class="material-icons me-2" style="font-size: 20px; vertical-align: middle;">vpn_key</i>
                  Cambiar contraseña
                </v-card-title>
                <v-card-text class="pt-4">
                  <v-form ref="passwordForm" @submit.prevent="savePassword">
                    <v-text-field
                      v-model="pwd.current"
                      label="Contraseña actual *"
                      variant="outlined"
                      density="comfortable"
                      :type="showPwd.current ? 'text' : 'password'"
                      :append-inner-icon="showPwd.current ? 'mdi-eye-off' : 'mdi-eye'"
                      hide-details="auto"
                      class="mb-3"
                      autocomplete="current-password"
                      @click:append-inner="showPwd.current = !showPwd.current"
                    />
                    <v-text-field
                      v-model="pwd.new"
                      label="Nueva contraseña *"
                      variant="outlined"
                      density="comfortable"
                      :type="showPwd.new ? 'text' : 'password'"
                      :append-inner-icon="showPwd.new ? 'mdi-eye-off' : 'mdi-eye'"
                      hint="Mínimo 8 caracteres"
                      persistent-hint
                      class="mb-3"
                      autocomplete="new-password"
                      @click:append-inner="showPwd.new = !showPwd.new"
                    />
                    <v-text-field
                      v-model="pwd.confirm"
                      label="Confirmar nueva contraseña *"
                      variant="outlined"
                      density="comfortable"
                      :type="showPwd.confirm ? 'text' : 'password'"
                      :append-inner-icon="showPwd.confirm ? 'mdi-eye-off' : 'mdi-eye'"
                      hide-details="auto"
                      class="mb-4"
                      autocomplete="new-password"
                      @click:append-inner="showPwd.confirm = !showPwd.confirm"
                    />
                    <v-btn
                      type="submit"
                      color="warning"
                      variant="flat"
                      :loading="savingPassword"
                      block
                    >
                      <i class="material-icons me-1" style="font-size: 18px; vertical-align: middle;">lock_reset</i>
                      Actualizar contraseña
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Firma -->
            <v-col cols="12" md="6">
              <v-card elevation="2" class="h-100">
                <v-card-title class="bg-grey-lighten-4 text-subtitle-1 py-3">
                  <i class="material-icons me-2" style="font-size: 20px; vertical-align: middle;">draw</i>
                  Firma para documentos
                </v-card-title>
                <v-card-text class="pt-4">
                  <p class="text-body-2 text-medium-emphasis mb-3">
                    Imagen de tu firma usada en contratos y documentos. Formatos: JPG, PNG.
                  </p>
                  <div v-if="firmaPreviewUrl" class="mb-3">
                    <p class="text-caption text-medium-emphasis mb-1">Firma actual</p>
                    <img :src="firmaPreviewUrl" alt="Firma" class="firma-preview rounded border" />
                  </div>
                  <div class="d-flex flex-wrap align-items-center gap-2">
                    <v-btn color="secondary" variant="outlined" @click="triggerFirmaInput">
                      <i class="material-icons me-1" style="font-size: 18px;">upload</i>
                      Subir nueva firma
                    </v-btn>
                    <input
                      ref="firmaInput"
                      type="file"
                      accept="image/*"
                      class="d-none"
                      @change="onFirmaSelected"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { me, updateProfile, changePassword, uploadUserSignature, showSuccess, showError } from '@/actions';
import { joinMediaBaseUrl } from '@/utils/helpers';

export default {
  name: 'UserProfile',
  data() {
    return {
      loading: true,
      saving: false,
      savingPassword: false,
      loadError: null,
      profile: null,
      form: {
        firstName: '',
        lastName: ''
      },
      pwd: {
        current: '',
        new: '',
        confirm: ''
      },
      showPwd: {
        current: false,
        new: false,
        confirm: false
      },
      rules: {
        required: (v) => (v != null && String(v).trim() !== '') || 'Campo obligatorio'
      }
    };
  },
  computed: {
    firmaPreviewUrl() {
      const f = this.profile?.firma;
      if (!f) return '';
      const base = process.env.VUE_APP_URL_IMAGE || process.env.VUE_APP_URL_SERVICES || '';
      return joinMediaBaseUrl(base, f);
    }
  },
  async mounted() {
    await this.loadProfile();
  },
  methods: {
    async loadProfile() {
      this.loading = true;
      this.loadError = null;
      try {
        const res = await me();
        const errors = res.data?.errors;
        if (errors?.length) {
          this.loadError = errors.map((e) => e.message).join(' ');
          return;
        }
        const user = res.data?.data?.me;
        if (!user) {
          this.loadError = 'No se pudo cargar tu perfil. Inicia sesión de nuevo.';
          return;
        }
        this.profile = user;
        this.form = {
          firstName: user.firstName || '',
          lastName: user.lastName || ''
        };
      } catch (e) {
        this.loadError = e.message || 'Error al cargar el perfil';
      } finally {
        this.loading = false;
      }
    },
    async saveProfile() {
      const formRef = this.$refs.profileForm;
      if (formRef && typeof formRef.validate === 'function') {
        const result = await formRef.validate();
        const valid = typeof result === 'boolean' ? result : result?.valid;
        if (valid === false) return;
      }
      if (!this.form.firstName?.trim() || !this.form.lastName?.trim()) {
        showError('Nombre y apellidos son obligatorios');
        return;
      }

      this.saving = true;
      try {
        const res = await updateProfile({
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim()
        });
        const payload = res.data?.data?.updateProfile;
        const err = payload?.error;
        if (err) {
          showError(err);
          return;
        }
        const user = payload?.user;
        if (user) {
          this.profile = { ...this.profile, ...user };
          this.$store.state.firstName = user.firstName;
          this.$store.state.lastName = user.lastName;
        }
        showSuccess('Nombre actualizado correctamente');
      } catch (e) {
        showError(e.message || 'Error al guardar');
      } finally {
        this.saving = false;
      }
    },
    async savePassword() {
      if (!this.pwd.current || !this.pwd.new || !this.pwd.confirm) {
        showError('Completa todos los campos de contraseña');
        return;
      }
      if (this.pwd.new.length < 8) {
        showError('La nueva contraseña debe tener al menos 8 caracteres');
        return;
      }
      if (this.pwd.new !== this.pwd.confirm) {
        showError('La confirmación no coincide con la nueva contraseña');
        return;
      }

      this.savingPassword = true;
      try {
        const res = await changePassword({
          currentPassword: this.pwd.current,
          newPassword: this.pwd.new
        });
        const payload = res.data?.data?.changePassword;
        const gqlErr = res.data?.errors?.[0]?.message;
        if (gqlErr) {
          showError(gqlErr);
          return;
        }
        if (!payload?.success) {
          showError(payload?.error || 'No se pudo cambiar la contraseña');
          return;
        }
        this.pwd = { current: '', new: '', confirm: '' };
        this.showPwd = { current: false, new: false, confirm: false };
        showSuccess('Contraseña actualizada correctamente');
      } catch (e) {
        showError(e.message || 'Error al cambiar la contraseña');
      } finally {
        this.savingPassword = false;
      }
    },
    triggerFirmaInput() {
      this.$refs.firmaInput?.click();
    },
    async onFirmaSelected(e) {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !file.type.startsWith('image/')) {
        showError('Selecciona un archivo de imagen');
        return;
      }
      if (!this.profile?.id) return;

      this.saving = true;
      try {
        const res = await uploadUserSignature(String(this.profile.id), file);
        const up = res.data?.data?.uploadUserSignature;
        if (!up?.success) {
          showError(up?.error || 'No se pudo subir la firma');
          return;
        }
        if (up.firmaUrl) {
          this.profile = { ...this.profile, firma: up.firmaUrl };
        } else {
          await this.loadProfile();
        }
        showSuccess('Firma actualizada');
      } catch (err) {
        showError(err.message || 'Error al subir la firma');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.user-profile-page {
  max-width: 1200px;
  margin: 0 auto;
}
.firma-preview {
  max-height: 160px;
  max-width: 100%;
  object-fit: contain;
}
</style>
